import { Button, FormHeading, FormSectionGroup, Layout } from '@vwfs-bronson/bronson-react';
import { t } from 'i18next';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { AddressFormNew } from '../../../../components/AddressFormNew';
import { AddressFormModel_MX } from '../../../../components/AddressFormNew/mixed/formModel';
import { BankDetailsNew } from '../../../../components/BankDetailsNew';
import { CustomerDetailsFormNew } from '../../../../components/CustomerDetailsFormNew';
import { EmploymentDetailsFormNew } from '../../../../components/EmploymentDetailsFormNew';
import { EmploymentDetailsFormModel_MX } from '../../../../components/EmploymentDetailsFormNew/mixed/formModel';
import { ExpenseDetailsForm } from '../../../../components/ExpenseDetailsForm';
import { LinkedRelationshipsForm_P_PL } from '../../../../components/LinkedRelationshipsNew/private/LinkedRelationships_P_PL';
import { PreviousAddressFormNew } from '../../../../components/PreviousAddressFormNew';
import { PreviousEmploymentDetailsFormNew } from '../../../../components/PreviousEmploymentDetailsFormNew';
import { isAtLeast3YearsApart } from '../../../../services/common/miscUtils';
import { journeyDataSelectors } from '../../../../services/redux/features/journeyData.slice';
import { useFormSectionHelpers } from '../../../utils';

const getCustomerFormsToRender = (
  showPreviousAddress: boolean,
  showPreviousEmployment: boolean,
  showBankDetails: boolean
) => {
  return [
    'customerDetails',
    'customerAddress',
    ...(showPreviousAddress ? (['previousCustomerAddress'] as const) : []),
    'employmentDetails',
    ...(showPreviousEmployment ? (['previousEmploymentDetails'] as const) : []),
    'expenseDetails',
    'linkedRelationship',
    ...(showBankDetails ? (['bankDetails'] as const) : []),
  ] as const;
};

const getCoCustomerFormsToRender = (showPreviousAddress: boolean, showPreviousEmployment: boolean) => {
  return [
    'customerDetails2',
    'customerAddress2',
    ...(showPreviousAddress ? (['previousCustomerAddress2'] as const) : []),
    'employmentDetails2',
    ...(showPreviousEmployment ? (['previousEmploymentDetails2'] as const) : []),
    'expenseDetails2',
    'linkedRelationship2',
  ] as const;
};

type FormId =
  | ReturnType<typeof getCustomerFormsToRender>[number]
  | ReturnType<typeof getCoCustomerFormsToRender>[number];

export const CustomerDataPrivateLease = () => {
  const [forms, setForms] = useState<ReadonlyArray<FormId>>(() => getCustomerFormsToRender(false, false, false));
  const [isAnotherHirerAdded, setIsAnotherHirerAdded] = useState(false);

  const [showPreviousAddress, setShowPreviousAddress] = useState(false);
  const [showPreviousCoCustomerAddress, setShowPreviousCoCustomerAddress] = useState(false);
  const [showPreviousEmployment, setShowPreviousEmployment] = useState(false);
  const [showPreviousCoCustomerEmployment, setShowPreviousCoCustomerEmployment] = useState(false);

  // When in Standalone Service Plan user selected Upfront payment for service plan,
  // then we do not need Bank Details

  const isUpfrontPayment = useSelector(journeyDataSelectors.isUpfrontPayment);

  const toggleAnotherHirer = () => {
    setIsAnotherHirerAdded((isAnotherHirerAdded) => !isAnotherHirerAdded);
  };

  const onCustomerAddressChange = (values: AddressFormModel_MX) => {
    const { timeAtAddressFrom, timeAtAddressTo } = values;
    if (!timeAtAddressFrom || !timeAtAddressTo) return;

    setShowPreviousAddress(!isAtLeast3YearsApart(timeAtAddressFrom, timeAtAddressTo));
  };

  const onCustomerAddress2Change = (values: AddressFormModel_MX) => {
    const { timeAtAddressFrom, timeAtAddressTo } = values;
    if (!timeAtAddressFrom || !timeAtAddressTo) return;

    setShowPreviousCoCustomerAddress(!isAtLeast3YearsApart(timeAtAddressFrom, timeAtAddressTo));
  };

  const onTimeWithEployerChange = (values: EmploymentDetailsFormModel_MX) => {
    const { timeWithEmployerFrom, timeWithEmployerTo } = values;
    if (!timeWithEmployerFrom || !timeWithEmployerTo) return;

    setShowPreviousEmployment(!isAtLeast3YearsApart(timeWithEmployerFrom, timeWithEmployerTo));
  };

  const onTimeWithEployer2Change = (values: EmploymentDetailsFormModel_MX) => {
    const { timeWithEmployerFrom, timeWithEmployerTo } = values;
    if (!timeWithEmployerFrom || !timeWithEmployerTo) return;

    setShowPreviousCoCustomerEmployment(!isAtLeast3YearsApart(timeWithEmployerFrom, timeWithEmployerTo));
  };

  useEffect(() => {
    setForms([
      ...getCustomerFormsToRender(showPreviousAddress, showPreviousEmployment, !isUpfrontPayment),
      ...(isAnotherHirerAdded
        ? getCoCustomerFormsToRender(showPreviousCoCustomerAddress, showPreviousCoCustomerEmployment)
        : []),
    ]);
  }, [
    isAnotherHirerAdded,
    isUpfrontPayment,
    showPreviousAddress,
    showPreviousCoCustomerAddress,
    showPreviousEmployment,
    showPreviousCoCustomerEmployment,
  ]);

  const getFormHelpers = useFormSectionHelpers(forms);

  return (
    <FormSectionGroup>
      <CustomerDetailsFormNew.PL formHelpers={getFormHelpers('customerDetails')} />
      <AddressFormNew.PL onValuesChange={onCustomerAddressChange} formHelpers={getFormHelpers('customerAddress')} />
      {showPreviousAddress && <PreviousAddressFormNew.PL formHelpers={getFormHelpers('previousCustomerAddress')} />}
      <EmploymentDetailsFormNew.PL
        formHelpers={getFormHelpers('employmentDetails')}
        onValuesChange={onTimeWithEployerChange}
      />
      {showPreviousEmployment && (
        <PreviousEmploymentDetailsFormNew.PL formHelpers={getFormHelpers('previousEmploymentDetails')} />
      )}
      <ExpenseDetailsForm.PL formHelpers={getFormHelpers('expenseDetails')} />
      <LinkedRelationshipsForm_P_PL
        formHelpers={getFormHelpers('linkedRelationship')}
        titleTranslationKey="linkedRelationshipsForm:title:private"
      />
      {!isUpfrontPayment && (
        <BankDetailsNew.PL
          formHelpers={getFormHelpers('bankDetails')}
          titleTranslationKey="bankDetailsForm:title:private"
        />
      )}

      {isAnotherHirerAdded && (
        <>
          <Layout>
            <Layout.Item default="1/2">
              <FormHeading>{t('customerDataPrivate:header:private2')}</FormHeading>
            </Layout.Item>
          </Layout>
          <CustomerDetailsFormNew.PL formHelpers={getFormHelpers('customerDetails2')} />
          <AddressFormNew.PL
            onValuesChange={onCustomerAddress2Change}
            formHelpers={getFormHelpers('customerAddress2')}
            isSecondHirer
          />
          {showPreviousCoCustomerAddress && (
            <PreviousAddressFormNew.PL formHelpers={getFormHelpers('previousCustomerAddress2')} isSecondHirer />
          )}
          <EmploymentDetailsFormNew.PL
            formHelpers={getFormHelpers('employmentDetails2')}
            onValuesChange={onTimeWithEployer2Change}
          />
          {showPreviousCoCustomerEmployment && (
            <PreviousEmploymentDetailsFormNew.PL formHelpers={getFormHelpers('previousEmploymentDetails2')} />
          )}
          <ExpenseDetailsForm.PL formHelpers={getFormHelpers('expenseDetails2')} />
          <LinkedRelationshipsForm_P_PL
            formHelpers={getFormHelpers('linkedRelationship2')}
            titleTranslationKey="linkedRelationshipsForm:title:private"
          />
        </>
      )}

      <Layout className="u-mb">
        <Layout.Item>
          <Button
            icon={isAnotherHirerAdded ? 'semantic-minus' : 'semantic-plus'}
            link
            secondary
            onClick={toggleAnotherHirer}
          >
            {isAnotherHirerAdded
              ? t('customerDataPrivate:removeAnotherHirerButton')
              : t('customerDataPrivate:addAnotherHirerButton')}
          </Button>
        </Layout.Item>
      </Layout>
    </FormSectionGroup>
  );
};
